import React, { useEffect, useState, useMemo } from 'react'
import * as api from '../../requests'
import { base64ToArrayBuffer } from '../Accounting/ReceiptValidation/ReceiptValidationActions'
import ReportInfo from './ReportInfo'
import { Spinner } from '../Loaders/Loaders'
import { pdfTypes } from './ReportViewerReducer'
import { Checkbox } from '@tecuity/granite-ui/Checkbox'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import CrossHeavy from '@tecuity/sos-icons/react/cross-heavy'
import RevertIcon from '@tecuity/sos-icons/react/revert'
import { FilingSourceTypeIds } from '../../Utilities/Utilities'

const ReportEndpoint = (props) => {
  const {
    pdfInfo,
    reportsMap,
    activeFilename,
    setActiveFilename,
    activePageIndex,
    selectFile,
    pageChanged,
    pages,
    endpointToFileHierarchy,
    setEndpointToFileHierarchy,
    failedToLoadMap,
    selectFailedToLoad,
    markEndpointSettled,
    reportEndpointFailures,
  } = props
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false)
  const [endpointErrorFileName, setEndpointErrorFileName] = useState('')
  const [endpointErrorMessage, setEndpointErrorMessage] = useState('')
  const [localList, setLocalList] = useState([])
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isFailClosed, setIsFailClosed] = useState(false)
  const [refreshReport, setRefreshReport] = useState(false)
  const [loadFailures, setLoadFailures] = useState([])

  const getReports = () => {
    const promiseArray = []
    switch (pdfInfo.type) {
      case pdfTypes.RECEIPT: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_RECEIPT_PDF(
              pdfInfo.ReceiptNum,
              (data) => {
                var arrrayBuffer = base64ToArrayBuffer(data.PDF ? data.PDF : data)
                var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                resolve({
                  files: [
                    {
                      filename: pdfInfo.filename,
                      bytes: blob,
                      info: data,
                      pdfInfoFileName: pdfInfo.filename,
                    },
                  ],
                })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.CORRESPONDENCE: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_CUSTOM_CORRESPONDENCE_PDF(
              pdfInfo.correspondenceTypeId,
              pdfInfo.customCorrespondenceId,
              (data) => {
                var arrrayBuffer = base64ToArrayBuffer(data.PDF ? data.PDF : data)
                var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                resolve({
                  files: [
                    {
                      filename: pdfInfo.filename,
                      bytes: blob,
                      info: data,
                      pdfInfoFileName: pdfInfo.filename,
                    },
                  ],
                })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.TRADEMARK: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_TRADEMARK_REPORTS(
              pdfInfo.trademarkAmendmentNumber,
              (data) => {
                const filesList = []
                data.forEach((file) => {
                  if (file.Description && file.PDF) {
                    var arrrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                    filesList.push({
                      filename: `${file.Description}-${
                        file.Description === 'Receipt Detail' && pdfInfo.ReceiptNum
                          ? pdfInfo.ReceiptNum
                          : pdfInfo.trademarkAmendmentNumber
                      }.pdf`,
                      bytes: blob,
                      info: file,
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.BUSINESS_ENTITY: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_BUSINESS_ENTITY_REPORTS(
              pdfInfo.AmendmentNum,
              (data) => {
                const filesList = []
                data.forEach((file) => {
                  if (file.Description && file.PDF) {
                    var arrrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                    filesList.push({
                      filename: `${file.Description}-${
                        file.Description === 'Receipt Detail' && pdfInfo.ReceiptNum
                          ? pdfInfo.ReceiptNum
                          : pdfInfo.AmendmentNum
                      }.pdf`,
                      bytes: blob,
                      info: file,
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.IMAGE_ENVELOPE: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_IMAGE_ENVELOPE_REPORTS(
              pdfInfo.imageEnvelopeId,
              (data) => {
                const reports = Array.isArray(data) ? data : data?.Reports ?? []
                const failures = Array.isArray(data) ? [] : data?.Failures ?? []
                const filesList = []
                const usedNames = new Set()
                reports.forEach((file, index) => {
                  if (file.Description && file.PDF) {
                    var arrrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                    let filename = file.FileName || `${file.Description}-${file.ImageNum ?? index}.pdf`
                    filename = `Envelope ${pdfInfo.imageEnvelopeId} - ${filename}`
                    if (usedNames.has(filename)) {
                      const base = filename.replace(/\.pdf$/i, '') + `-${index}`
                      filename = `${base}.pdf`
                      let suffix = 2
                      while (usedNames.has(filename)) {
                        filename = `${base}-${suffix}.pdf`
                        suffix++
                      }
                    }
                    usedNames.add(filename)
                    filesList.push({
                      filename,
                      bytes: blob,
                      info: file,
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList, failures })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.DISTRIBUTION_REPORT: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.GET_DIST_POST_REPORT(
              pdfInfo.batchNum,
              (data) => {
                const filesList = []
                data.forEach((file) => {
                  if (file.Description && file.PDF) {
                    var arrrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                    filesList.push({
                      filename: file.Description + '.pdf',
                      bytes: blob,
                      info: file,
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.BATCH_REPORT: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.GET_POST_BATCH_REPORT(
              pdfInfo.depositNum,
              (data) => {
                const filesList = []
                data.forEach((file) => {
                  if (file.Description && file.PDF) {
                    var arrrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                    filesList.push({
                      filename: file.Description + '.pdf',
                      bytes: blob,
                      info: file,
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.RECONCILE_REPORT: {
        const nums = pdfInfo.batchNums || []
        nums.forEach((batchNum) => {
          promiseArray.push(
            new Promise((resolve, reject) => {
              api.GET_PRINTABLE_BATCH_REPORT(
                pdfInfo.selectedUserID,
                batchNum,
                (data) => {
                  var arrrayBuffer = base64ToArrayBuffer(data.PDF ? data.PDF : data)
                  var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                  resolve({
                    files: [
                      {
                        filename: `Reconcile-Batch-${batchNum}.pdf`,
                        bytes: blob,
                        info: data,
                        pdfInfoFileName: pdfInfo.filename,
                      },
                    ],
                  })
                },
                (err) => {
                  reject({ err, pdfInfo })
                }
              )
            })
          )
        })
        break
      }
      case pdfTypes.UCC1: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_UCC_REPORTS(
              pdfInfo.uccAmendmentNumber,
              (data) => {
                const filesList = []
                data.forEach((file) => {
                  if (file.Description && file.PDF) {
                    var arrrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                    filesList.push({
                      filename: `${file.Description}-${
                        file.Description === 'Receipt Detail' && pdfInfo.ReceiptNum
                          ? pdfInfo.ReceiptNum
                          : pdfInfo.uccAmendmentNumber
                      }.pdf`,
                      bytes: blob,
                      info: file,
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.ORDER: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_ORDER_REPORTS(
              pdfInfo.orderNum,
              (data) => {
                const filesList = []
                data.forEach((file) => {
                  if (file.Description && file.PDF) {
                    var arrrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                    filesList.push({
                      filename: `${file.Description}-${
                        file.Description === 'Receipt Detail' && pdfInfo.ReceiptNum
                          ? pdfInfo.ReceiptNum
                          : pdfInfo.orderNum
                      }.pdf`,
                      bytes: blob,
                      info: file,
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.NOTARY: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_NOTARY_REPORTS(
              pdfInfo.filingNum,
              (data) => {
                const filesList = []
                data.forEach((file) => {
                  if (file.Description && file.PDF) {
                    var arrrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                    filesList.push({
                      filename: `${file.Description}-${
                        file.Description === 'Receipt Detail' && pdfInfo.ReceiptNum
                          ? pdfInfo.ReceiptNum
                          : pdfInfo.filingNum
                      }.pdf`,
                      bytes: blob,
                      info: file,
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.APOSTILLE: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_APOSTILLE_REPORTS(
              pdfInfo.apostilleNum,
              (data) => {
                const filesList = []
                data.forEach((file) => {
                  if (file.Description && file.PDF) {
                    var arrrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                    filesList.push({
                      filename: `${file.Description}-${
                        file.Description === 'Receipt Detail' && pdfInfo.ReceiptNum
                          ? pdfInfo.ReceiptNum
                          : pdfInfo.apostilleNum
                      }.pdf`,
                      bytes: blob,
                      info: file,
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.FACSIMILE: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_FACSIMILE_REPORTS(
              pdfInfo.facsimileNum,
              (data) => {
                const filesList = []
                data.forEach((file) => {
                  if (file.Description && file.PDF) {
                    var arrrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                    filesList.push({
                      filename: `${file.Description}-${
                        file.Description === 'Receipt Detail' && pdfInfo.ReceiptNum
                          ? pdfInfo.ReceiptNum
                          : pdfInfo.facsimileNum
                      }.pdf`,
                      bytes: blob,
                      info: file,
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.REGISTERED_AGENT: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_REGISTERED_AGENT_REPORTS(
              pdfInfo.registeredAgentNum,
              (data) => {
                const filesList = []
                data.forEach((file) => {
                  if (file.Description && file.PDF) {
                    var arrrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                    filesList.push({
                      filename: `${file.Description}-${
                        file.Description === 'Receipt Detail' && pdfInfo.ReceiptNum
                          ? pdfInfo.ReceiptNum
                          : pdfInfo.facsimileNum
                      }.pdf`,
                      bytes: blob,
                      info: file,
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case pdfTypes.SERVICE_OF_PROCESS: {
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_SERVICE_OF_PROCESS_REPORTS(
              pdfInfo.FilingNum,
              (data) => {
                const filesList = []
                data.forEach((file) => {
                  if (file.Description && file.PDF) {
                    var arrrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                    filesList.push({
                      filename: `${file.Description}-${pdfInfo.FilingNum}.pdf`,
                      bytes: blob,
                      info: file,
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      case FilingSourceTypeIds.Charity:
      case FilingSourceTypeIds.Contractor:
      case FilingSourceTypeIds.Lobbyist: 
      case FilingSourceTypeIds.AthleteAgent:
      case FilingSourceTypeIds.Miscellaneous:
      {
        const sourceTypeName = Object.keys(FilingSourceTypeIds).find(x => FilingSourceTypeIds[x] === pdfInfo.type) 
        if (pdfInfo.ReceiptNum && pdfInfo.ReceiptNum !== '') {
          promiseArray.push(
            new Promise((resolve, reject) => {
              api.DOWNLOAD_RECEIPT_PDF(
                pdfInfo.ReceiptNum,
                (data) => {
                  var arrrayBuffer = base64ToArrayBuffer(data.PDF ? data.PDF : data)
                  var blob = new Blob([arrrayBuffer], { type: 'application/pdf' })
                  resolve({
                    files: [
                      {
                        filename: `${sourceTypeName} ${pdfInfo.filingNum} Receipt.pdf`,
                        bytes: blob,
                        info: { ...data, Description: `${sourceTypeName} ${pdfInfo.filingNum} ${data.Description}` },
                        pdfInfoFileName: pdfInfo.filename,
                      },
                    ],
                  })
                },
                (err) => {
                  console.error(err)
                  reject({ err, pdfInfo })
                }
              )
            }))
        }
        promiseArray.push(
          new Promise((resolve, reject) => {
            api.DOWNLOAD_REPORTS(
              pdfInfo.type,
              pdfInfo.filingNum,
              (data) => {
                const filesList = [];
                data.forEach((file) => {
                  if (file.Description && file.PDF) {
                    var arrayBuffer = base64ToArrayBuffer(file.PDF)
                    var blob = new Blob([arrayBuffer], { type: `application/pdf` })
                    filesList.push({
                      filename: `${sourceTypeName} ${pdfInfo.filingNum} ${file.FileName}`,
                      bytes: blob,
                      info: { ...file, Description: `${sourceTypeName} ${pdfInfo.filingNum} ${file.Description}` },
                      pdfInfoFileName: pdfInfo.filename,
                    })
                  }
                })
                resolve({ files: filesList })
              },
              (err) => {
                console.error(err)
                reject({ err, pdfInfo })
              }
            )
          })
        )
        break
      }
      default: {
        console.warn('Type not valid. File not loaded.', pdfInfo)
      }
    }

    setShowLoadingSpinner(true)
    setLoadFailures([])
    Promise.all(promiseArray)
      .then((reportsData) => {
        const tempList = []
        const collectedFailures = []
        reportsData.forEach((info) => {
          if (info.failures) collectedFailures.push(...info.failures)
          info.files.forEach((file) => {
            if (endpointToFileHierarchy[pdfInfo.filename]) {
              if (!endpointToFileHierarchy[pdfInfo.filename].includes(file.filename))
                endpointToFileHierarchy[pdfInfo.filename].push(file.filename)
            } else {
              endpointToFileHierarchy[pdfInfo.filename] = [file.filename]
            }
            reportsMap[file.filename] = file
            tempList.push(file)
          })
        })
        setLocalList(tempList)
        setLoadFailures(collectedFailures)
        reportEndpointFailures(pdfInfo.filename, collectedFailures)
        markEndpointSettled(pdfInfo.filename, tempList.length > 0 ? 'loaded' : 'empty')
        setShowLoadingSpinner(false)
        if (failedToLoadMap[pdfInfo.filename]) {
          selectFailedToLoad(pdfInfo, true)
        }
      })
      .catch((info) => {
        console.error(`Error in ${info.pdfInfo.filename}`, info.err)
        reportEndpointFailures(pdfInfo.filename, [])
        setLoadFailures([])
        markEndpointSettled(pdfInfo.filename, 'error')
        selectFailedToLoad({ ...pdfInfo, isChecked: false })
        setEndpointErrorMessage(
          info.err.Message || info.err.message || info.err.detail || info.err.Detail || info.err.title
        )
        setEndpointErrorFileName(info.pdfInfo.filename)
        setShowLoadingSpinner(false)
      })
  }

  useEffect(() => {
    getReports()
  }, [pdfInfo])

  useEffect(() => {
    if (refreshReport) {
      getReports()
      setRefreshReport(false)
      setEndpointErrorFileName('')
      setEndpointErrorMessage('')
    }
  }, [refreshReport])

  useEffect(() => {
    if (failedToLoadMap[pdfInfo.filename] && failedToLoadMap[pdfInfo.filename].IsClosed) {
      setIsFailClosed(true)
    }
  }, [failedToLoadMap, pdfInfo])

  const closeFile = (info = {}) => {
    if (!info.filename) {
      setIsFailClosed(true)
      localStorage.setItem(
        'REPORTS_LIST',
        JSON.stringify(
          (JSON.parse(localStorage.getItem('REPORTS_LIST')) || []).filter(
            (report) => report.filename !== pdfInfo.filename
          )
        )
      )
    }
    if (endpointToFileHierarchy[pdfInfo.filename].length <= 1) {
      localStorage.setItem(
        'REPORTS_LIST',
        JSON.stringify(
          (JSON.parse(localStorage.getItem('REPORTS_LIST')) || []).filter(
            (report) => report.filename !== pdfInfo.filename
          )
        )
      )
      let tempMap = { ...endpointToFileHierarchy }
      delete tempMap[pdfInfo.filename]
      setEndpointToFileHierarchy(tempMap)
    } else {
      setEndpointToFileHierarchy({
        ...endpointToFileHierarchy,
        [pdfInfo.filename]: endpointToFileHierarchy[pdfInfo.filename].filter(
          (filename) => filename !== info.filename
        ),
      })
    }
    delete reportsMap[info.filename]
    delete pages[info.filename]
    setActiveFilename(null)
  }

  const filteredFiles = useMemo(() => {
    return localList.filter((file) =>
      (endpointToFileHierarchy[pdfInfo.filename] || []).includes(file.filename)
    )
  }, [endpointToFileHierarchy, localList, pdfInfo])

  const selectFailedToLoadCheckbox = (e) => {
    selectFailedToLoad({ ...pdfInfo, isChecked: e })
  }

  if (isFailClosed) return null

  return (
    <div>
      {endpointErrorFileName && ( //endpointErrorFileName
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
          <div className='sidebar-header'>
            Failed To Load File:
            <button
              onClick={() => {
                setRefreshReport(true)
              }}
              title={`Reload ${pdfInfo.filename}`}
            >
              <RevertIcon />
            </button>
            <button
              onClick={() => {
                setShowDeleteConfirm((show) => !show)
              }}
              title={`Close ${pdfInfo.filename}`}
            >
              <CrossHeavy />
            </button>
          </div>
          <div className="row">
            <div className="report-file-header-is-selected failed-to-load">
              <Checkbox
                className="report-file-header-checkbox"
                ariaLabel={`is ${endpointErrorFileName} selected`}
                checked={!!failedToLoadMap[pdfInfo.filename].isChecked}
                onChange={(e) => selectFailedToLoadCheckbox(e)}
              />
            </div>
            <p style={{ marginLeft: '2px', marginTop: '15px' }}>{endpointErrorFileName}</p>
          </div>
          {endpointErrorMessage && (
            <p style={{ marginLeft: '15px', marginTop: '8px' }}>{endpointErrorMessage}</p>
          )}
        </div>
      )}
      {showDeleteConfirm && (
        <div className="report-delete-confirm">
          <span>Are you sure you want to close this file?</span>
          <div className="report-delete-confirm-actions">
            <ContextButton
              variant={'success'}
              onClick={() => {
                setShowDeleteConfirm(false)
                closeFile()
              }}
            >
              Yes
            </ContextButton>
            <ContextButton
              variant={'danger'}
              onClick={() => {
                setShowDeleteConfirm(false)
              }}
            >
              No
            </ContextButton>
          </div>
        </div>
      )}
      {showLoadingSpinner && (
        <div style={{ margin: '30px' }}>
          <Spinner color="#7792d0" className="entity-step-loader" size="small" />
        </div>
      )}
      {!showLoadingSpinner && loadFailures.length > 0 && (
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
          <div className="sidebar-header">
            {localList.length === 0
              ? 'No reports could be loaded for this envelope:'
              : 'Some reports could not be loaded:'}
          </div>
          <ul style={{ marginLeft: '15px', marginTop: '8px' }}>
            {loadFailures.map((failure, i) => (
              <li key={`failure-${i}`}>
                {`${failure.ImageNum || failure.FilingNum || failure.Description}`}
                {failure.Message ? ` — ${failure.Message}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!showLoadingSpinner &&
        filteredFiles.map((info, i) => {
          return (
            <ReportInfo
              reportInfo={info}
              key={`pdf-${i}`}
              bytes={info.bytes || []}
              isActive={info.filename === activeFilename}
              activePageIndex={activePageIndex}
              onSelectFile={() => selectFile(info)}
              onSelectPage={(pageIndex) => selectFile(info, pageIndex)}
              pageChanged={pageChanged}
              pages={pages[info.filename]}
              closeFile={() => closeFile(info)}
            />
          )
        })}
    </div>
  )
}

export default ReportEndpoint
